import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Sparkles, Wand2, TrendingUp, Zap, Shield, Check,
  Star, Rocket, Play, Image as ImageIcon,
} from "lucide-react";
import MarketingNavbar from "@/components/MarketingNavbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const stats = [
  { k: "10M+", v: "views generated" },
  { k: "38%", v: "avg. engagement lift" },
  { k: "4.9★", v: "creator rating" },
  { k: "72h", v: "faster to publish" },
];

const bentoFeatures = [
  { icon: Wand2, title: "AI Script Studio V3", desc: "16-part viral kit — title, hook, scene-by-scene breakdown, b-roll, camera angles, hashtags and a viral score. Export as PDF.", span: "md:col-span-7" },
  { icon: TrendingUp, title: "Trend Radar", desc: "Live Google Trends by country, language and niche — with AI-crafted hooks and video ideas.", span: "md:col-span-5" },
  { icon: ImageIcon, title: "Thumbnail Studio", desc: "AI-rendered thumbnails plus 10 viral titles, palette, font style, and a CTR score.", span: "md:col-span-5" },
  { icon: Zap, title: "One brief. Ready to shoot.", desc: "Every generation is production-ready: caption, hashtags, CTA, overlays and posting time — done.", span: "md:col-span-7" },
];

const testimonials = [
  { name: "Ana Ruiz", role: "Creator · 480K", quote: "TikBoost turned my content chaos into a system. My weekly output tripled and so did the reach.", img: "https://images.unsplash.com/photo-1748015879337-ef95556c3749?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBjb250ZW50JTIwY3JlYXRvciUyMHBvcnRyYWl0JTIwc3R1ZGlvfGVufDB8fHx8MTc4NDk3ODMyNnww&ixlib=rb-4.1.0&q=85" },
  { name: "Marcus Vidal", role: "Founder · Fadetones", quote: "The trend radar alone paid for the year. We caught a sound the day it broke and got 3M views.", img: "https://images.unsplash.com/photo-1558730234-d8b2281b0d00?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBjb250ZW50JTIwY3JlYXRvciUyMHBvcnRyYWl0JTIwc3R1ZGlvfGVufDB8fHx8MTc4NDk3ODMyNnww&ixlib=rb-4.1.0&q=85" },
  { name: "Priya S.", role: "Editor · Loop Studio", quote: "The Thumbnail Studio is unreal — ten titles, a real image and a palette I can hand straight to my designer.", img: "https://images.unsplash.com/photo-1648493696785-c0b27b0d93a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBjb250ZW50JTIwY3JlYXRvciUyMHBvcnRyYWl0JTIwc3R1ZGlvfGVufDB8fHx8MTc4NDk3ODMyNnww&ixlib=rb-4.1.0&q=85" },
];

const pricing = [
  { name: "Free", price: "£0", desc: "Get started, no card required.", features: ["10 AI credits / month", "Script Studio V3", "Generation history"], cta: "Start free" },
  { name: "Creator", price: "£19", highlight: true, desc: "For serious solo creators.", features: ["500 AI credits / month", "Trend Radar (live)", "Thumbnail Studio", "PDF exports & analytics", "Priority AI models"], cta: "Start 7-day free trial" },
  { name: "Pro", price: "£49", desc: "For teams and power users.", features: ["Unlimited AI credits (fair use)", "Everything in Creator", "Fastest generation", "Future AI tools included", "Priority support"], cta: "Upgrade to Pro" },
];

const faqs = [
  { q: "Do I need to install anything?", a: "No. TikBoost AI runs in your browser. Sign in and start generating." },
  { q: "Which platforms are supported?", a: "TikTok, Instagram Reels and YouTube Shorts, with more on the roadmap." },
  { q: "Can I cancel any time?", a: "Yes. Plans are month-to-month with a 7-day trial on paid tiers. No contracts." },
  { q: "Is my content private?", a: "Yes. Your uploads and outputs are yours. We never train on your data." },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <MarketingNavbar />

      {/* HERO */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden">
        <div className="aurora" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400 border border-cyan-500/40 rounded-full px-3 py-1">
              <Sparkles className="w-3 h-3" /> New · AI Script Studio V3
            </span>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              Grow smarter on TikTok — <span className="bg-gradient-to-r from-cyan-300 via-cyan-400 to-pink-400 bg-clip-text text-transparent">without the burnout</span>.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl leading-relaxed">
              TikBoost AI turns your ideas into a system: research trends, write hooks, cut clips
              and schedule posts — all inside one elegant workspace, built for creators who ship daily.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Button asChild size="lg" className="rounded-full bg-gradient-to-r from-cyan-400 to-pink-500 text-zinc-950 hover:brightness-110 border-0 h-12 px-7" data-testid="hero-cta-primary">
                <Link to="/register">Start free <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
              <Button asChild size="lg" variant="ghost" className="rounded-full h-12 px-6" data-testid="hero-cta-secondary">
                <Link to="/features"><Play className="w-4 h-4 mr-2" /> See how it works</Link>
              </Button>
            </div>

            <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl">
              {stats.map((s) => (
                <div key={s.v} className="border-l border-border pl-4">
                  <p className="font-display text-2xl font-semibold">{s.k}</p>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{s.v}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* BENTO FEATURES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="landing-features">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Features</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-medium tracking-tight max-w-xl">A calm workspace for a loud algorithm.</h2>
          </div>
          <Link to="/features" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-2">
            Explore all features <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {bentoFeatures.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className={`${f.span} col-span-1 group relative rounded-2xl border border-border bg-card p-8 overflow-hidden hover:border-cyan-500/40 transition-colors`}
              data-testid={`feature-tile-${i}`}
            >
              <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-cyan-500/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <f.icon className="w-6 h-6 text-cyan-400" />
              <h3 className="mt-6 font-display text-xl sm:text-2xl font-medium">{f.title}</h3>
              <p className="mt-3 text-muted-foreground leading-relaxed max-w-md">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="landing-pricing">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Pricing</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-medium tracking-tight">Simple pricing. Real leverage.</h2>
          <p className="mt-4 text-muted-foreground">Start free. Upgrade when you're ready to ship faster.</p>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {pricing.map((p) => (
            <div
              key={p.name}
              className={`relative rounded-2xl border p-8 flex flex-col ${
                p.highlight ? "border-cyan-500/50 bg-card shadow-[0_0_60px_-15px_rgba(6,182,212,0.35)]" : "border-border bg-card"
              }`}
              data-testid={`pricing-tier-${p.name.toLowerCase()}`}
            >
              {p.highlight && (
                <span className="absolute -top-3 left-8 text-[10px] uppercase tracking-widest font-semibold bg-gradient-to-r from-cyan-400 to-pink-500 text-zinc-950 rounded-full px-3 py-1">
                  Most popular
                </span>
              )}
              <p className="font-display text-lg">{p.name}</p>
              <p className="mt-6 font-display text-4xl font-semibold">
                {p.price}
                <span className="text-base text-muted-foreground font-normal">/mo</span>
              </p>
              <p className="text-sm text-muted-foreground mt-2">{p.desc}</p>
              <ul className="mt-6 space-y-3 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                asChild
                className={`mt-8 rounded-full ${p.highlight ? "bg-gradient-to-r from-cyan-400 to-pink-500 text-zinc-950 border-0 hover:brightness-110" : "bg-secondary text-foreground hover:bg-secondary/80"}`}
                data-testid={`pricing-cta-${p.name.toLowerCase()}`}
              >
                <Link to="/register">{p.cta}</Link>
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="landing-testimonials">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Loved by creators</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-medium tracking-tight">A quiet upgrade to your content stack.</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t) => (
            <figure key={t.name} className="rounded-2xl border border-border bg-card p-8 flex flex-col gap-6">
              <div className="flex items-center gap-1 text-cyan-400">
                {[0,1,2,3,4].map((i) => <Star key={i} className="w-4 h-4 fill-current" />)}
              </div>
              <blockquote className="text-foreground leading-relaxed">"{t.quote}"</blockquote>
              <figcaption className="flex items-center gap-3 mt-auto">
                <img src={t.img} alt={t.name} className="w-10 h-10 rounded-full object-cover grayscale" />
                <div>
                  <p className="text-sm font-medium">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.role}</p>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-24" data-testid="landing-faq">
        <p className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">FAQ</p>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-medium tracking-tight">Everything you might ask.</h2>
        <Accordion type="single" collapsible className="mt-10">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-border">
              <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="relative overflow-hidden rounded-3xl border border-border bg-card p-10 md:p-16">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-pink-500/20 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-cyan-500/20 blur-3xl" />
          <div className="relative max-w-2xl">
            <Rocket className="w-6 h-6 text-cyan-400" />
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight">Ship better content, on repeat.</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">Set up in under a minute. Cancel any time.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button asChild className="rounded-full bg-gradient-to-r from-cyan-400 to-pink-500 text-zinc-950 border-0 hover:brightness-110" data-testid="cta-signup">
                <Link to="/register">Create free account</Link>
              </Button>
              <Button asChild variant="ghost" className="rounded-full" data-testid="cta-contact">
                <Link to="/contact">Talk to us</Link>
              </Button>
            </div>
            <p className="mt-6 text-xs text-muted-foreground flex items-center gap-2"><Shield className="w-3 h-3" /> No credit card required</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
